import React from 'react'

import * as Yup from 'yup'

import { Column, InnerLoading, Row } from '@shared/components/Layout'
import { Container, Content } from './styles'
import { FiLock, FiMail, FiPhone, FiUser } from 'react-icons/fi'
import { Input, InputMasked } from '@shared/components/Form'
import { api, fetchBase64 } from '@shared/services/api'
import { Form } from '@unform/web'
import { useAuth } from '@shared/hooks/auth'
import { useToast } from '@shared/hooks/toast'

import AuthService from '@shared/services/AuthService'
import Button from '@shared/components/Button'
import Header from '@shared/layouts/Partials/Header'
import Message from '@shared/components/Message'
import getValidationErrors from '@shared/utils/getValidationErrors'

const Profile = () => {
  const formRef = React.useRef(null)
  const authRef = React.useRef(null)

  const [isLoadingQRCode, setIsLoadingQRCode] = React.useState(true)
  const [isSaving, setIsSaving] = React.useState(false)
  const [isSavingAuth, setIsSavingAuth] = React.useState(false)
  const [isAuthenticated, setIsAuthenticated] = React.useState(false)
  const [authenticatorQRCode, setAuthenticatorQRCode] = React.useState(false)
  const [isChecking2fa, setIsCheckin2fa] = React.useState(false)

  const { addToast } = useToast()
  const { user } = useAuth()

  React.useEffect(function checkTwoFactorAuthentication () {
    setIsCheckin2fa(true)
    api
      .get('/auth/2fa/check')
      .then(() => {
        setIsAuthenticated(true)
      })
      .catch(() => {
        setIsAuthenticated(false)
      })
      .finally(() => {
        setIsCheckin2fa(false)
      })
  }, [])

  React.useEffect(function getAuthenticatorURL () {
    api
      .get('/auth/2fa')
      .then(async ({ data: url }) => {
        const image = await fetchBase64(
          `https://chart.googleapis.com/chart?chs=250x250&cht=qr&chl=${url}`
        )

        setAuthenticatorQRCode(image)
      }).finally(() => {
        setIsLoadingQRCode(false)
      })
  }, [])

  const handleAuthenticatorEnable = React.useCallback(
    async ({ secret }) => {
      try {
        if (isAuthenticated) return

        const payload = { secret }

        setIsSavingAuth(true)

        authRef.current?.setErrors({})

        const schema = Yup.object().shape({
          secret: Yup.string().required(
            'Informe o código do Authenticator'
          )
        })

        await schema.validate(payload, { abortEarly: false })

        await api.post('/auth/2fa', payload)

        setIsAuthenticated(true)

        addToast({
          type: 'success',
          title: 'Authenticator habilitado!'
        })
      } catch (error) {
        if (error instanceof Yup.ValidationError) {
          formRef.current?.setErrors(getValidationErrors(error))
        } else {
          console.error(error)

          if (typeof error.response?.data?.message === 'string') {
            addToast({
              type: 'error',
              title: error.response?.data?.message
            })
          } else {
            addToast({
              type: 'error',
              title: 'Erro habilitar o authenticator!',
              description:
                'Verifique o código e tente novamente em alguns instantes.'
            })
          }
        }
      } finally {
        setIsSavingAuth(false)
      }
    },
    [addToast, isAuthenticated]
  )

  const handleProfileUpdate = React.useCallback(
    async ({
      password_current,
      password,
      password_confirmation
    }) => {
      try {
        const authService = new AuthService()

        const formData = {
          password_current,
          password,
          password_confirmation
        }

        setIsSaving(true)

        formRef.current?.setErrors({})

        const requirePassword = {
          is: (val) => !!val,
          then: Yup.string().required('Campo Obrigatório'),
          otherwise: Yup.string()
        }

        const schema = Yup.object().shape({
          name: Yup.string().required('Nome obrigatório'),
          phone: Yup.string().required('Telefone obrigatório'),
          email: Yup.string()
            .required('Email obrigatório')
            .email('Digite um e-mail válido'),
          password_current: Yup.string(),
          password: Yup.string().when('password_current', requirePassword),
          password_confirmation: Yup.string()
            .when('password_current', requirePassword)
            .oneOf([Yup.ref('password'), undefined], 'As senhas não coicidem')
        })

        await schema.validate(formData, { abortEarly: false })

        const profileData = formData

        if (profileData.password) {
          const { data } = await api.post('/auth/change_password', profileData)

          addToast({
            type: 'success',
            title: 'Senha atualizada!'
          })

          authService.updateAuthData({ data })
        }

        const { data } = await api.patch('/auth/update_profile', profileData)

        addToast({
          type: 'success',
          title: 'Perfil atualizado!'
        })

        // updateUser(data.user)
        authService.setUser(data.user)
      } catch (error) {
        if (error instanceof Yup.ValidationError) {
          formRef.current?.setErrors(getValidationErrors(error))
        } else {
          console.error(error)
          addToast({
            type: 'error',
            title: 'Erro atualizar perfil!',
            description:
              'Verifique os dados e tente novamente em alguns instantes.'
          })
        }
      } finally {
        setIsSaving(false)
      }
    },
    [addToast]
  )

  return (
    <Container>
      {/* TODO: set correct goback according to the current module */}

      <Header title="Editar Perfil" navigateBack />

      <Content>
        <Row>
          <Column xs={12} md={5}>
            <Form
              ref={formRef}
              className="sign-up-form"
              onSubmit={handleProfileUpdate}
              initialData={{
                name: user.name,
                email: user.email,
                phone: user.phone
              }}>
              <h1 className="sign-up-form__title">Meus Dados</h1>

              <Input
                disabled
                name="name"
                type="text"
                icon={FiUser}
                placeholder="Nome"
              />

              <Input
                disabled
                name="email"
                type="text"
                icon={FiMail}
                placeholder="E-mail"
              />

              <Input
                disabled
                name="phone"
                type="text"
                icon={FiPhone}
                placeholder="Telefone"
              />

              <Input
                containerStyle={{ marginTop: '2rem' }}
                name="password_current"
                type="password"
                icon={FiLock}
                placeholder="Senha"
              />

              <Input
                name="password"
                type="password"
                icon={FiLock}
                placeholder="Nova Senha"
              />

              <Input
                name="password_confirmation"
                type="password"
                icon={FiLock}
                placeholder="Confirmar Nova Senha"
              />

              <Button loading={isSaving} size="large" nativeType="submit" full>
                Confirmar Mudanças
              </Button>
            </Form>
          </Column>

          <Column xs={12} md={7}>
            <InnerLoading visible={isChecking2fa || isLoadingQRCode} fullWidth>
              <Column xs={12} xxl={10}>
                <Form
                  className="sign-up-form"
                  style={{ minWidth: '100%' }}
                  onSubmit={handleAuthenticatorEnable}
                  ref={authRef}>
                  <h1 className="sign-up-form__title">
                    Autenticação de Dois Fatores
                  </h1>

                  {isAuthenticated ? (
                    <>
                      <Message type="success" className="no-print">
                        A autenticação de dois fatores já está habilitada em sua
                        empresa!
                      </Message>
                      <p>
                        Com o QR Code ativado, a empresa confirma que armazena
                        com segurança os dados de seus clientes. Isso permitirá
                        que a sua empresa execute pagamentos com mais segurança.
                      </p>
                    </>
                  ) : (
                    <>
                      <p>
                        Com o QR Code ativado, a empresa confirma que armazena
                        com segurança os dados de seus clientes. O código gerado
                        a partir do procedimento abaixo permitirá que a empresa
                        execute pagamentos de forma mais segura.
                        <br />
                        <br />
                        <b>
                          Escaneie o código abaixo com o app Google
                          Authenticator (ou similar) para gerar seu código de
                          verificação:
                        </b>
                      </p>

                      {authenticatorQRCode ? (
                        <img src={authenticatorQRCode} alt="" />
                      ) : (
                        <div
                          style={{
                            height: '250px',
                            width: '250px',
                            backgroundColor: 'rgba(0,0,0,.05)'
                          }}
                        />
                      )}

                      <Row>
                        <Column xs={8}>
                          <InputMasked
                            name="secret"
                            mask={'000 000'}
                            icon={FiLock}
                            placeholder="Código do Authenticator"
                          />
                        </Column>
                        <Column xs={4}>
                          <Button
                            loading={isSavingAuth}
                            size="large"
                            nativeType="submit"
                            full
                            style={{ margin: 0 }}>
                            Salvar
                          </Button>
                        </Column>
                      </Row>
                    </>
                  )}
                </Form>
              </Column>
            </InnerLoading>
          </Column>
        </Row>
      </Content>
    </Container>
  )
}

export default Profile
